import type { Editor } from "@tiptap/react";
import type { EditorView } from "prosemirror-view";

import {
  chatAssetItemSchema,
  chatProductItemSchema,
} from "schemas/chatMessage";

// NOTE (Bobby, 2025-06-30): This function takes the tip tap editor state and builds an easier string to work with both for the llm and for us to parse + render
// components with.
export const getCleanedChatPrompt = (editor: Editor) => {
  let result = "";

  editor.state.doc.descendants((node) => {
    if (node.type.name === "text") {
      result += node.text;
    } else if (node.type.name === "assetComponent") {
      const validChatAssetItem = chatAssetItemSchema.safeParse(node.attrs.data);
      if (validChatAssetItem.success) {
        result += `<chat-token>{
            "type": "asset",
            "data": ${JSON.stringify(validChatAssetItem.data)}
        }</chat-token>`;
      }
    } else if (node.type.name === "productComponent") {
      const validChatProductItem = chatProductItemSchema.safeParse(
        node.attrs.data,
      );
      if (validChatProductItem.success) {
        result += `<chat-token>{
            "type": "product",
            "data": ${JSON.stringify(validChatProductItem.data)}
        }</chat-token>`;
      }
    }
  });

  return result;
};

export const chatEditorOnUpdate = (
  contextMenuOpen: boolean,
  setAtPosition: (position: number | null) => void,
  setContextMenuOpen: (open: boolean) => void,
  atPosition: number | null,
) => {
  return ({ editor }: { editor: Editor }) => {
    const content = editor.getText();
    const cursorPos = editor.state.selection.anchor;

    // Check if @ was just typed
    if (content[cursorPos - 1] === "@" && !contextMenuOpen) {
      setAtPosition(cursorPos - 1);
      setContextMenuOpen(true);
    }

    // Check if @ was deleted from the tracked position
    if (atPosition !== null && content[atPosition] !== "@") {
      setContextMenuOpen(false);
      setAtPosition(null);
    }
  };
};

export const chatEditorHandleKeyDown = (
  contextMenuOpen: boolean,
  setAtPosition: (position: number | null) => void,
  setContextMenuOpen: (open: boolean) => void,
  atPosition: number | null,
  onSubmit: (e: React.FormEvent) => Promise<void>,
) => {
  return (view: EditorView, event: KeyboardEvent) => {
    const { state } = view;
    const { selection } = state;

    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !contextMenuOpen &&
      onSubmit
    ) {
      event.preventDefault();
      void onSubmit(event as unknown as React.FormEvent);
      return true;
    }

    // Handle @ key
    if (event.key === "@" && !contextMenuOpen) {
      setTimeout(() => {
        setAtPosition(selection.anchor);
        setContextMenuOpen(true);
      }, 0);
    }

    // Handle Escape to close menu
    if (event.key === "Escape" && contextMenuOpen) {
      setContextMenuOpen(false);
      setAtPosition(null);
      event.preventDefault();
      return true;
    }

    // Handle Backspace - check if we're deleting the @
    if (event.key === "Backspace" && contextMenuOpen && atPosition !== null) {
      const textBefore = state.doc.textBetween(atPosition, atPosition + 1);
      if (textBefore === "@") {
        // We're about to delete the @, close the menu
        setContextMenuOpen(false);
        setAtPosition(null);
      }
    }

    // Handle Space to close menu without selection
    if (event.key === " " && contextMenuOpen) {
      setContextMenuOpen(false);
      setAtPosition(null);
    }

    return false;
  };
};
